import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import { Star, Clock, LogIn, LogOut } from "lucide-react";
import { ProgressBar } from "./ProgressBar";
import { loginRequest } from "@/lib/msal";
import { deriveInitials } from "@/store/authStore";
import logoUrl from "@/assets/neusim-logo.svg";

interface HeaderProps {
  /** Show the segmented progress bar (only during an active attempt). Default false. */
  showProgress?: boolean;
  /** Show the XP + countdown-timer pills (only during an active attempt). Default false. */
  showStats?: boolean;
  totalDecisions?: number;
  currentDecision?: number;
  xp?: number;
  timer?: string; // "MM:SS"
}

function parseSeconds(timer: string): number {
  const [m, s] = timer.split(":").map(Number);
  return (m ?? 0) * 60 + (s ?? 0);
}

export function Header({
  showProgress = false,
  showStats = false,
  totalDecisions = 0,
  currentDecision = 0,
  xp = 0,
  timer = "00:00",
}: HeaderProps): JSX.Element {
  const seconds = parseSeconds(timer);
  const timerColor =
    seconds < 60 ? "text-[#f87272]" : seconds < 300 ? "text-[#f59e0b]" : "text-white";

  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0]);

  const userName = isAuthenticated && account?.name !== undefined ? account.name : null;
  const userInitials = userName !== null ? deriveInitials(userName) : null;

  const handleAuthClick = (): void => {
    if (isAuthenticated) {
      void instance.logoutRedirect({ account: account ?? undefined }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[auth] logoutRedirect failed:", err);
      });
    } else {
      // eslint-disable-next-line no-console
      console.info("[auth] Sign in clicked — calling loginRedirect");
      void instance.loginRedirect(loginRequest).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[auth] loginRedirect failed:", err);
        window.alert(`Sign-in error: ${err instanceof Error ? err.message : String(err)}`);
      });
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-16 border-b border-[#e5e7eb] bg-white">
      <div className="mx-auto flex h-full max-w-[1440px] items-center gap-6 px-6">
        {/* Left */}
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
            <img src={logoUrl} alt="NeuSim logo" className="h-full w-full object-contain" />
          </div>
          <span className="hidden text-sm font-semibold text-[#0f1117] sm:inline">NeuSim</span>
        </div>

        {/* Center */}
        <div className="hidden flex-1 justify-center md:flex">
          {showProgress && (
            <div className="w-full max-w-md">
              <ProgressBar total={totalDecisions} current={currentDecision} />
            </div>
          )}
        </div>

        {/* Right */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {showStats && (
            <>
              {/* Mobile combined pill */}
              <div className="flex items-center gap-1.5 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-3 py-1.5 md:hidden">
                <Star className="h-3.5 w-3.5 text-[#7c8cf8]" />
                <span className="font-mono text-xs text-[#0f1117]">{xp}</span>
                <span className="text-[#d1d5db]">·</span>
                <Clock
                  className={`h-3.5 w-3.5 ${timerColor === "text-white" ? "text-[#0f1117]" : timerColor}`}
                />
                <span
                  className={`font-mono text-xs ${timerColor === "text-white" ? "text-[#0f1117]" : timerColor}`}
                >
                  {timer}
                </span>
              </div>

              {/* Desktop pills */}
              <div className="hidden items-center gap-1.5 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-3 py-1.5 md:flex">
                <Star className="h-3.5 w-3.5 text-[#7c8cf8]" />
                <span className="font-mono text-xs font-medium text-[#0f1117]">{xp} XP</span>
              </div>
              <div className="hidden items-center gap-1.5 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-3 py-1.5 md:flex">
                <Clock
                  className={`h-3.5 w-3.5 ${timerColor === "text-white" ? "text-[#0f1117]" : timerColor}`}
                />
                <span
                  className={`font-mono text-xs font-medium ${timerColor === "text-white" ? "text-[#0f1117]" : timerColor}`}
                >
                  {timer}
                </span>
              </div>
            </>
          )}

          {/* Auth pill — Sign in when signed out, user info when signed in */}
          <button
            type="button"
            onClick={handleAuthClick}
            title={isAuthenticated ? `Signed in as ${account?.username ?? userName ?? ""} — click to sign out` : "Sign in"}
            className="flex items-center gap-2 rounded-full border border-[#e5e7eb] bg-[#f9fafb] py-1 pl-1 pr-3 transition-colors hover:border-[#7c8cf8] hover:bg-white"
          >
            {userInitials !== null ? (
              <>
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#7c8cf8] to-[#a855f7] text-[11px] font-semibold text-white">
                  {userInitials}
                </div>
                <span className="hidden text-xs font-medium text-[#0f1117] sm:inline">
                  {userName}
                </span>
                <LogOut className="hidden h-3 w-3 text-[#8890b8] sm:inline" />
              </>
            ) : (
              <>
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 text-white">
                  <LogIn className="h-3.5 w-3.5" />
                </div>
                <span className="hidden text-xs font-semibold text-[#0f1117] sm:inline">
                  Sign in
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
